import React, { useMemo, useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMount } from 'react-use';
import { Box, Button, Grid, Typography } from '@mui/material';
import { getWindow, trackNavigatedToPage } from '@surfline/web-common';

import AbilityLevelsSpotDescriptor from 'components/AbilityLevelsSpotDescriptor';
import Breadcrumbs from 'components/Breadcrumbs';
import IdealConditions from 'components/IdealConditions';
import MetaTags from 'components/MetaTags';
import PageLoading from 'components/PageLoading';
import SpotGuideMoreInfo from 'components/SpotGuideMoreInfo';
import SpotSatelliteWithPin from 'components/SpotSatelliteWithPin';
import SpotTravelArticle from 'components/SpotTravelArticle';
import SpotDescriptor from 'components/SpotDescriptor';
import TravelGuideNotes from 'components/TravelGuideNotes';
import { useMaxWidthTabletLarge, useMaxWidthTablet } from 'hooks/useMediaQueries';
import { getSpotDetails, getSpotLoading } from 'selectors/spot';
import { useAppSelector } from 'stores/hooks';
import type { SpotDetails } from 'types/spot';
import resolveBreadcrumbs from 'utils/urls/breadcrumbResolver';
import { spotGuidePath } from 'utils/urls/spotGuidePath';

import styles from './SpotGuidePage.module.scss';

const SpotGuidePage: React.FC = () => {
  const { asPath } = useRouter();
  const router = useRouter();
  const [isNavigating, setIsNavigating] = useState(false);
  const spot: SpotDetails = useAppSelector(getSpotDetails);
  const spotLoading: boolean = useAppSelector(getSpotLoading);

  const isMobile = useMaxWidthTablet();
  const isMaxWidthTabletLarge = useMaxWidthTabletLarge();

  const isSpotLoading: boolean = useMemo(
    () => !!spotLoading || isNavigating,
    [spotLoading, isNavigating],
  );

  const metaTitle = useMemo(() => `${spot?.name} Surf Guide`, [spot]);
  const metaDescription = useMemo(
    () =>
      `Guide to the best surf conditions for ${spot?.name} including swell direction, wind, and tide, plus travel details like best season, water quality and parking.`,
    [spot],
  );
  const metaUrlPath = useMemo(
    () => spotGuidePath({ spotName: spot?.name, spotId: spot?._id }),
    [spot],
  );

  const spotName = useMemo(() => spot?.name, [spot]);
  const spotGeo = useMemo(() => ({ lat: spot?.lat, lon: spot?.lon }), [spot]);

  const idealConditions = useMemo(
    () => ({
      size: {
        description: spot?.travelDetails?.best?.size?.description,
      },
      swellDirection: {
        description: spot?.travelDetails?.best?.swellDirection?.description,
      },
      tide: {
        description: spot?.travelDetails?.best?.tide?.description,
      },
      windDirection: {
        description: spot?.travelDetails?.best?.windDirection?.description,
      },
    }),
    [spot],
  );

  const shouldShowIdealConditions = useMemo(
    () => Object.values(idealConditions).some((condition) => condition.description),
    [idealConditions],
  );

  const shouldShowTravelGuideNotes = useMemo(
    () =>
      spot?.travelDetails?.hazards ||
      spot?.travelDetails?.boardTypes?.length ||
      spot?.travelDetails?.access ||
      spot?.travelDetails?.bottom?.description ||
      spot?.travelDetails?.best?.season?.description,
    [spot?.travelDetails],
  );

  const guideDescription = useMemo(() => spot?.travelDetails?.description, [spot]);

  useMount(() => {
    trackNavigatedToPage('Spot Guide', {
      path: asPath,
      referrer: `${getWindow()?.location?.href ?? ''}${asPath}`,
      search: getWindow()?.location?.search,
      title: metaTitle,
      url: `${getWindow()?.location?.href ?? ''}${asPath}`,
      category: 'cams & reports',
      spotId: spot?._id,
      spotName: spot?.name,
      subregionId: spot?.subregion?._id,
      isMobileView: isMobile,
    });
  });

  useEffect(() => {
    const handleRouteChangeStart = () => {
      setIsNavigating(true);
    };

    const handleRouteChangeComplete = () => {
      setIsNavigating(false);
    };

    const handleRouteChangeError = () => {
      setIsNavigating(false);
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeError', handleRouteChangeError);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeError', handleRouteChangeError);
    };
  }, [router]);

  return (
    <Box>
      {isSpotLoading ? (
        <Box className="sl-section-container">
          <PageLoading />
        </Box>
      ) : (
        <Box>
          <MetaTags title={metaTitle} description={metaDescription} urlPath={metaUrlPath} />
          <Box className="sl-section-container">
            <Grid container spacing={2} className={styles.guideHeader}>
              <Grid mobile={12} tabletLg={5} item className={styles.geo}>
                <Box className={styles.mapWrapper} data-testid="spot-guide-map-wrapper">
                  <SpotSatelliteWithPin spotName={spotName} geo={spotGeo} size="large" />
                </Box>
                <Box className={styles.mapsLinkWrapper} data-testid="spot-guide-maps-link-wrapper">
                  <Link href={`https://www.google.com/maps?q=${spot.lat},${spot.lon}&z=17/`}>
                    <Button
                      variant="primary"
                      color={isMaxWidthTabletLarge ? 'inherit' : 'dark'}
                      className={styles.mapsButton}
                      disableRipple
                      disableFocusRipple
                      disableTouchRipple
                    >
                      View on Google Maps
                    </Button>
                  </Link>
                </Box>
              </Grid>
              <Grid mobile={12} tabletLg={7} item className={styles.idealConditions}>
                {shouldShowIdealConditions ? (
                  <IdealConditions conditions={idealConditions} />
                ) : (
                  <Box
                    className={styles.moreInfo}
                    data-testid="spot-guide-more-info-no-ideal-conditions"
                  >
                    <SpotGuideMoreInfo spotName={spotName} />
                  </Box>
                )}
              </Grid>
            </Grid>
            {spot.travelDetails && (
              <Box className={styles.travelDetails}>
                {spot.travelDetails.relatedArticleId && (
                  <Box data-testid="spot-guide-travel-article">
                    <SpotTravelArticle articleId={spot.travelDetails.relatedArticleId} />
                  </Box>
                )}
                <Box className={styles.descriptionWrapper}>
                  <Typography component="h3" variant="title3" className={styles.descriptionTitle}>
                    {spot.name} Surf Guide
                  </Typography>
                  <Typography
                    component="p"
                    variant="body"
                    className={styles.description}
                    dangerouslySetInnerHTML={{ __html: `${guideDescription}` }}
                    data-testid="spot-guide-description"
                  />
                </Box>
                <Grid container spacing={8} className={styles.descriptors}>
                  {spot.travelDetails?.abilityLevels?.description && (
                    <Grid
                      mobile={12}
                      tablet={6}
                      tabletLg={4}
                      item
                      className={styles.descriptorWrapper}
                      data-testid="spot-guide-ability-levels-descriptor"
                    >
                      <AbilityLevelsSpotDescriptor
                        abilityLevels={spot.travelDetails.abilityLevels}
                      />
                    </Grid>
                  )}
                  {spot.travelDetails?.localVibe?.description && (
                    <Grid
                      mobile={12}
                      tablet={6}
                      tabletLg={4}
                      item
                      className={styles.descriptorWrapper}
                      data-testid="spot-guide-local-vibe-descriptor"
                    >
                      <SpotDescriptor
                        kind="Local Vibe"
                        value={spot.travelDetails.localVibe.summary}
                        options={['Welcoming', 'Intimidating']}
                        description={spot.travelDetails.localVibe.description}
                        scale={spot.travelDetails.localVibe.rating}
                      />
                    </Grid>
                  )}
                  {spot.travelDetails?.crowdFactor?.description && (
                    <Grid
                      mobile={12}
                      tablet={6}
                      tabletLg={4}
                      item
                      className={styles.descriptorWrapper}
                      data-testid="spot-guide-crowd-factor-descriptor"
                    >
                      <SpotDescriptor
                        kind="Crowd Factor"
                        value={spot.travelDetails.crowdFactor?.summary}
                        options={['Mellow', 'Heavy']}
                        description={spot.travelDetails.crowdFactor?.description}
                        scale={spot.travelDetails.crowdFactor.rating}
                      />
                    </Grid>
                  )}
                  {spot.travelDetails?.spotRating?.description && (
                    <Grid
                      mobile={12}
                      tablet={6}
                      tabletLg={4}
                      item
                      className={styles.descriptorWrapper}
                      data-testid="spot-guide-spot-rating-descriptor"
                    >
                      <SpotDescriptor
                        kind="Spot Rating"
                        value={spot.travelDetails?.spotRating?.summary}
                        options={['Poor', 'Perfect']}
                        description={spot.travelDetails?.spotRating?.description}
                        scale={spot.travelDetails?.spotRating.rating}
                      />
                    </Grid>
                  )}
                  {spot.travelDetails?.shoulderBurn?.description && (
                    <Grid
                      mobile={12}
                      tablet={6}
                      tabletLg={4}
                      item
                      className={styles.descriptorWrapper}
                      data-testid="spot-guide-shoulder-burn-descriptor"
                    >
                      <SpotDescriptor
                        kind="Shoulder Burn"
                        value={spot.travelDetails.shoulderBurn?.summary}
                        options={['Light', 'Exhausting']}
                        description={spot.travelDetails.shoulderBurn?.description}
                        scale={spot.travelDetails.shoulderBurn.rating}
                      />
                    </Grid>
                  )}
                  {spot.travelDetails?.waterQuality?.description && (
                    <Grid
                      mobile={12}
                      tablet={6}
                      tabletLg={4}
                      item
                      className={styles.descriptorWrapper}
                      data-testid="spot-guide-water-quality-descriptor"
                    >
                      <SpotDescriptor
                        kind="Water Quality"
                        value={spot.travelDetails.waterQuality?.summary}
                        options={['Clean', 'Dirty']}
                        description={spot.travelDetails.waterQuality?.description}
                        scale={spot.travelDetails.waterQuality.rating}
                      />
                    </Grid>
                  )}
                </Grid>
                {shouldShowTravelGuideNotes && (
                  <Box
                    className={styles.travelGuideNotesWrapper}
                    data-testid="spot-guide-travel-guide-notes-wrapper"
                  >
                    <TravelGuideNotes
                      hazards={spot?.travelDetails?.hazards}
                      boardTypes={spot?.travelDetails?.boardTypes}
                      access={spot?.travelDetails?.access}
                      bottom={spot?.travelDetails?.bottom?.description}
                      bestSeason={spot?.travelDetails?.best?.season?.description}
                    />
                  </Box>
                )}
              </Box>
            )}
            {shouldShowIdealConditions && (
              <Box data-testid="spot-guide-more-info-with-ideal-conditions">
                <SpotGuideMoreInfo spotName={spotName} />
              </Box>
            )}
            <Breadcrumbs breadcrumbs={resolveBreadcrumbs(spot.breadcrumb)} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SpotGuidePage;
